import React, { useMemo, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
} from '@material-ui/core';
import TableSortLabel from '@material-ui/core/TableSortLabel';

const GET_ADMIN_SPRINT_ITEMS_PAGED = gql`
  query AdminSprintItemsPaged($status: String, $blockedOnly: Boolean, $dueFrom: ISO8601DateTime, $dueTo: ISO8601DateTime, $managerId: ID, $userId: ID, $limit: Int, $offset: Int, $orderBy: String, $order: String) {
    adminSprintItemsPaged(status: $status, blockedOnly: $blockedOnly, dueFrom: $dueFrom, dueTo: $dueTo, managerId: $managerId, userId: $userId, limit: $limit, offset: $offset, orderBy: $orderBy, order: $order) {
      total_count
      nodes {
        id
        title
        status
        dueAt
        cepaCategory
        sprintIds
      }
    }
  }
`;

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    paddingTop: theme.spacing(8),
  },
  header: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  filters: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
}));

const SprintItemsIndex = () => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    status: '',
    blockedOnly: false,
    dueFrom: '',
    dueTo: '',
    managerId: '',
    userId: '',
  });

  const variables = useMemo(() => ({
    status: filters.status || null,
    blockedOnly: filters.blockedOnly || null,
    dueFrom: filters.dueFrom || null,
    dueTo: filters.dueTo || null,
    managerId: filters.managerId || null,
    userId: filters.userId || null,
  }), [filters]);

  const [orderBy, setOrderBy] = useState('due_at');
  const [order, setOrder] = useState('asc');
  const { data, loading, error } = useQuery(GET_ADMIN_SPRINT_ITEMS_PAGED, {
    variables: { ...variables, limit: rowsPerPage, offset: page * rowsPerPage, orderBy, order },
    fetchPolicy: 'cache-and-network',
  });

  const connection = data?.adminSprintItemsPaged;
  const allRows = connection?.nodes || [];
  const totalCount = connection?.total_count || 0;
  const pagedRows = allRows;

  const setFilter = (key, value) => {
    setPage(0);
    setFilters((f) => ({ ...f, [key]: value }));
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h4">Sprint Items</Typography>
        <Box>
          <Button variant="contained" color="primary" component="a" href="/admin/sprint_items.csv">
            Export CSV
          </Button>
        </Box>
      </Box>

      <Paper>
        <Box className={classes.filters} p={2}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filters.status} onChange={(e) => setFilter('status', e.target.value)} label="Status">
              <MenuItem value="">All</MenuItem>
              <MenuItem value="OPEN">OPEN</MenuItem>
              <MenuItem value="IN_PROGRESS">IN_PROGRESS</MenuItem>
              <MenuItem value="COMPLETED">COMPLETED</MenuItem>
              <MenuItem value="BLOCKED">BLOCKED</MenuItem>
              <MenuItem value="WONT_DO">WONT_DO</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={(
              <Checkbox
                checked={!!filters.blockedOnly}
                onChange={(e) => setFilter('blockedOnly', e.target.checked)}
                color="primary"
              />
            )}
            label="Blocked Only"
          />
          <TextField
            variant="outlined"
            size="small"
            type="datetime-local"
            label="Due From"
            InputLabelProps={{ shrink: true }}
            value={filters.dueFrom}
            onChange={(e) => setFilter('dueFrom', e.target.value)}
          />
          <TextField
            variant="outlined"
            size="small"
            type="datetime-local"
            label="Due To"
            InputLabelProps={{ shrink: true }}
            value={filters.dueTo}
            onChange={(e) => setFilter('dueTo', e.target.value)}
          />
          <TextField
            variant="outlined"
            size="small"
            label="Manager ID"
            value={filters.managerId}
            onChange={(e) => setFilter('managerId', e.target.value)}
            style={{ minWidth: 140 }}
          />
          <TextField
            variant="outlined"
            size="small"
            label="User ID"
            value={filters.userId}
            onChange={(e) => setFilter('userId', e.target.value)}
            style={{ minWidth: 140 }}
          />
        </Box>

        <Box className={classes.tableContainer}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}><Typography>Loading…</Typography></Box>
          ) : error ? (
            <Box p={2}><Typography color="error">Error loading items</Typography></Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={orderBy==='title'?order:false}>
                      <TableSortLabel active={orderBy==='title'} direction={orderBy==='title'?order:'asc'} onClick={() => { setOrderBy('title'); setOrder(order==='asc'?'desc':'asc'); setPage(0); }}>
                        Title
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={orderBy==='status'?order:false}>
                      <TableSortLabel active={orderBy==='status'} direction={orderBy==='status'?order:'asc'} onClick={() => { setOrderBy('status'); setOrder(order==='asc'?'desc':'asc'); setPage(0); }}>
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={orderBy==='due_at'?order:false}>
                      <TableSortLabel active={orderBy==='due_at'} direction={orderBy==='due_at'?order:'asc'} onClick={() => { setOrderBy('due_at'); setOrder(order==='asc'?'desc':'asc'); setPage(0); }}>
                        Due
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Sprints</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.map((i) => (
                    <TableRow key={i.id}>
                      <TableCell>{i.title}</TableCell>
                      <TableCell>{i.status}</TableCell>
                      <TableCell>{i.dueAt || '-'}</TableCell>
                      <TableCell>{i.cepaCategory}</TableCell>
                      <TableCell>{(i.sprintIds || []).join(', ')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default SprintItemsIndex;


